import { useEffect, useState } from "react";
import { gameApi } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Trash2, Lock, Unlock, Loader2 } from "lucide-react";

export const FramesAdmin = () => {
  const [frames, setFrames] = useState(null);
  const [name, setName] = useState("");
  const [access, setAccess] = useState("free");
  const [ent, setEnt] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = () => gameApi.get("/game/admin/frames").then((r) => setFrames(r.data.frames)).catch(() => setFrames([]));
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!name.trim()) { toast.error("Enter a frame name first"); e.target.value = ""; return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file); fd.append("name", name.trim());
      fd.append("access", access); fd.append("required_entitlement", ent.trim());
      await gameApi.post("/game/admin/frames", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Frame uploaded!"); setName(""); setEnt(""); setAccess("free"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const patch = async (id, body) => {
    try { await gameApi.post(`/game/admin/frames/${id}`, body); load(); }
    catch { toast.error("Update failed"); }
  };
  const remove = async (id) => {
    try { await gameApi.delete(`/game/admin/frames/${id}`); toast.success("Frame removed"); load(); }
    catch { toast.error("Delete failed"); }
  };

  return (
    <div className="mt-3 space-y-3" data-testid="admin-frames">
      <div className="rounded-xl border border-border bg-card p-3">
        <p className="text-sm font-bold text-primary">Upload custom frame</p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">Transparent PNG, <b>512×512px</b>. Keep the center ~360×360px clear (avatar shows through); art in the outer ring only. Max 5MB.</p>
        <input data-testid="frame-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Frame name (e.g. Champion Laurel)"
          className="mt-2 w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
        <div className="mt-2 flex gap-2">
          <select data-testid="frame-access-select" value={access} onChange={(e) => setAccess(e.target.value)}
            className="rounded-lg border border-border bg-background px-2 py-2 text-sm">
            <option value="free">Free for all</option>
            <option value="locked">Locked</option>
          </select>
          {access === "locked" && (
            <input data-testid="frame-ent-input" value={ent} onChange={(e) => setEnt(e.target.value)} placeholder="Entitlement id (e.g. level_coat_100)"
              className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm" />
          )}
        </div>
        <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-2.5 text-xs font-bold text-primary" data-testid="frame-upload-label">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Uploading…" : "Choose PNG & upload"}
          <input type="file" accept="image/png,image/webp" className="hidden" data-testid="frame-upload-input" onChange={upload} disabled={uploading} />
        </label>
      </div>

      {!frames ? <div className="py-6 text-center"><Loader2 className="mx-auto animate-spin text-muted-foreground" /></div> : frames.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">No custom frames yet.</p>
      ) : frames.map((f) => (
        <div key={f.id} data-testid={`admin-frame-${f.id}`} className={`flex items-center gap-3 rounded-xl border border-border bg-card p-2.5 ${f.active ? "" : "opacity-50"}`}>
          <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-lg border border-border bg-muted">
            <img src={f.image_path} alt={f.name} className="h-full w-full object-contain" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-primary">{f.name}</p>
            <p className="text-[11px] text-muted-foreground">{f.access === "locked" ? `Locked · ${f.required_entitlement || "no entitlement set"}` : "Free for all"}{f.active ? "" : " · inactive"}</p>
          </div>
          <button data-testid={`frame-toggle-access-${f.id}`} onClick={() => patch(f.id, { access: f.access === "locked" ? "free" : "locked" })}
            className="shrink-0 rounded-lg border border-border px-2 py-1.5 text-xs font-bold text-primary">
            {f.access === "locked" ? <Unlock size={13} /> : <Lock size={13} />}
          </button>
          <button data-testid={`frame-toggle-active-${f.id}`} onClick={() => patch(f.id, { active: !f.active })}
            className="shrink-0 rounded-lg border border-border px-2 py-1.5 text-xs font-bold text-primary">{f.active ? "Hide" : "Show"}</button>
          <button data-testid={`frame-delete-${f.id}`} onClick={() => remove(f.id)}
            className="shrink-0 rounded-lg bg-destructive px-2 py-1.5 text-xs font-bold text-white"><Trash2 size={13} /></button>
        </div>
      ))}
    </div>
  );
};
